<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">Стримы пользователей</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadStreams({ page: 0 })">
            <i class="bi bi-arrow-repeat me-1"></i>
            Обновить
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetFilters">
            Сбросить фильтры
          </button>
        </div>
      </div>

      <div class="row g-2 mt-1">
        <div class="col-12 col-lg-2">
          <input v-model.trim="filters.userId" type="number" min="1" class="form-control form-control-sm" placeholder="User ID" />
        </div>
        <div class="col-12 col-lg-3">
          <input v-model.trim="filters.username" type="text" class="form-control form-control-sm" placeholder="Username" />
        </div>
        <div class="col-12 col-lg-3">
          <input v-model.trim="filters.type" type="text" class="form-control form-control-sm" placeholder="Тип стрима" />
        </div>
        <div class="col-6 col-lg-2">
          <select v-model.number="pagination.size" class="form-select form-select-sm" @change="loadStreams({ page: 0 })">
            <option :value="10">10 / стр.</option>
            <option :value="20">20 / стр.</option>
            <option :value="50">50 / стр.</option>
          </select>
        </div>
        <div class="col-6 col-lg-2 d-grid">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="loadStreams({ page: 0 })">
            Применить
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div class="row g-3 mt-1">
        <div class="col-12 col-xxl-8">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th><button type="button" class="admin-sort-btn" @click="toggleSort('name')">Name {{ sortMark('name') }}</button></th>
                  <th><button type="button" class="admin-sort-btn" @click="toggleSort('type')">Type {{ sortMark('type') }}</button></th>
                  <th><button type="button" class="admin-sort-btn" @click="toggleSort('ownerUsername')">Owner {{ sortMark('ownerUsername') }}</button></th>
                  <th><button type="button" class="admin-sort-btn" @click="toggleSort('contentsCount')">Contents {{ sortMark('contentsCount') }}</button></th>
                  <th>
                    <button type="button" class="admin-sort-btn" @click="toggleSort('publishedContentsCount')">
                      Published {{ sortMark('publishedContentsCount') }}
                    </button>
                  </th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="streams.length">
                <tr
                  v-for="stream in streams"
                  :key="stream.id"
                  :class="{ 'admin-table__row--active': selectedStreamId === stream.id }"
                  @click="openDetails(stream.id)"
                >
                  <td>{{ stream.name || '—' }}</td>
                  <td>{{ stream.type || '—' }}</td>
                  <td>{{ stream.ownerUsername || '—' }}</td>
                  <td>{{ stream.contentsCount }}</td>
                  <td>{{ stream.publishedContentsCount }}</td>
                  <td class="text-end" @click.stop>
                    <div class="btn-group btn-group-sm">
                      <button class="btn btn-outline-primary" @click="openDetails(stream.id)">Просмотр</button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center text-muted py-3">
                    {{ isLoading ? 'Загрузка...' : 'Стримы не найдены.' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="admin-pagination mt-2">
            <div class="text-muted small">
              Всего: {{ pagination.totalElements }} | Страница {{ displayPage }} / {{ paginationDisplayTotalPages }}
            </div>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" :disabled="isLoading || !canGoPrev" @click="goToPrevPage">
                Назад
              </button>
              <button type="button" class="btn btn-outline-secondary" :disabled="isLoading || !canGoNext" @click="goToNextPage">
                Вперед
              </button>
            </div>
          </div>
        </div>

        <div class="col-12 col-xxl-4">
          <div v-if="selectedStream" class="admin-detail">
            <div class="admin-detail__title">Стрим #{{ selectedStream.id }}</div>
            <small class="text-muted">
              Владелец: {{ selectedStream.ownerUsername || '—' }} (ID: {{ selectedStream.ownerUserId || '—' }})
            </small>

            <form class="mt-2" @submit.prevent="submitStreamUpdate">
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label mb-1">Name</label>
                  <input v-model.trim="streamForm.name" :disabled="isLoading" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Description</label>
                  <textarea v-model="streamForm.description" :disabled="isLoading" rows="2" class="form-control form-control-sm"></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Type</label>
                  <input v-model.trim="streamForm.type" disabled type="text" class="form-control form-control-sm" />
                </div>
              </div>

              <div class="mt-2">
                <AdminJsonTextarea
                  v-model="settingsText"
                  label="Settings JSON"
                  help-text="Пусто = не изменять settings."
                  @validity-change="settingsValidation = $event"
                />
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="reloadSelectedStream">
                  Перезагрузить
                </button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">Сохранить</button>
              </div>
            </form>
          </div>

          <div v-else class="admin-empty-state">Выберите стрим из таблицы для просмотра и редактирования.</div>
        </div>
      </div>

      <div v-if="selectedStreamId" class="admin-content-surface mt-3">
        <div class="admin-content-surface__head">
          <div class="admin-detail__title">Контент стрима #{{ selectedStreamId }}</div>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            :disabled="isLoading"
            @click="loadStreamContents({ page: 0 })"
          >
            Обновить контент
          </button>
        </div>

        <div class="row g-2 mt-1">
          <div class="col-12 col-lg-2">
            <select
              v-model.number="contentsPagination.size"
              class="form-select form-select-sm"
              :disabled="isLoading"
              @change="loadStreamContents({ page: 0 })"
            >
              <option :value="10">10 / стр.</option>
              <option :value="20">20 / стр.</option>
              <option :value="50">50 / стр.</option>
            </select>
          </div>
          <div class="col-12 col-lg-8">
            <div class="admin-statuses">
              <label v-for="status in CONTENT_STATUS_VALUES" :key="`status-${status}`" class="form-check form-check-inline mb-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :checked="isStatusSelected(status)"
                  :disabled="isLoading"
                  @change="toggleStatusFilter(status)"
                />
                <span class="form-check-label">{{ status }}</span>
              </label>
            </div>
          </div>
          <div class="col-12 col-lg-2 d-grid">
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="clearStatusFilters">
              Все статусы
            </button>
          </div>
        </div>

        <div class="table-responsive admin-table-wrap mt-2">
          <table class="table table-sm align-middle mb-0 admin-table">
            <thead>
              <tr>
                <th>
                  <button type="button" class="admin-sort-btn" @click="toggleContentSort('id')">
                    ID {{ contentSortMark('id') }}
                  </button>
                </th>
                <th>
                  <button type="button" class="admin-sort-btn" @click="toggleContentSort('title')">
                    Title {{ contentSortMark('title') }}
                  </button>
                </th>
                <th>
                  <button type="button" class="admin-sort-btn" @click="toggleContentSort('status')">
                    Status {{ contentSortMark('status') }}
                  </button>
                </th>
                <th>
                  <button type="button" class="admin-sort-btn" @click="toggleContentSort('publishedAt')">
                    Published {{ contentSortMark('publishedAt') }}
                  </button>
                </th>
                <th>
                  <button type="button" class="admin-sort-btn" @click="toggleContentSort('updatedAt')">
                    Updated {{ contentSortMark('updatedAt') }}
                  </button>
                </th>
                <th class="text-end">Действия</th>
              </tr>
            </thead>
            <tbody v-if="streamContents.length">
              <tr
                v-for="content in streamContents"
                :key="content.id"
                :class="{ 'admin-table__row--active': selectedContentId === content.id }"
              >
                <td>{{ content.id }}</td>
                <td>{{ content.title || '—' }}</td>
                <td>{{ content.status || '—' }}</td>
                <td>{{ formatDateTime(content.publishedAt) }}</td>
                <td>{{ formatDateTime(content.updatedAt) }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="isLoading"
                    @click="openContentDetails(content.id)"
                  >
                    Просмотр
                  </button>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="6" class="text-center text-muted py-3">
                  {{ isLoading ? 'Загрузка контента...' : 'Контент не найден.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="admin-pagination mt-2">
          <div class="text-muted small">
            Всего: {{ contentsPagination.totalElements }} | Страница {{ contentDisplayPage }} / {{ contentPaginationDisplayTotalPages }}
          </div>
          <div class="btn-group btn-group-sm">
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="isLoading || !canGoContentsPrev"
              @click="goToContentsPrevPage"
            >
              Назад
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="isLoading || !canGoContentsNext"
              @click="goToContentsNextPage"
            >
              Вперед
            </button>
          </div>
        </div>

        <div class="admin-content-detail mt-3">
          <div v-if="selectedContent">
            <div class="admin-detail__subtitle">Контент #{{ selectedContent.id }}</div>
            <div class="row g-2 small">
              <div class="col-12 col-lg-4"><strong>Status:</strong> {{ selectedContent.status || '—' }}</div>
              <div class="col-12 col-lg-4"><strong>Slug:</strong> {{ selectedContent.slug || '—' }}</div>
              <div class="col-12 col-lg-4"><strong>External ID:</strong> {{ selectedContent.externalId || '—' }}</div>
              <div class="col-12 col-lg-6"><strong>Created:</strong> {{ formatDateTime(selectedContent.createdAt) }}</div>
              <div class="col-12 col-lg-6"><strong>Updated:</strong> {{ formatDateTime(selectedContent.updatedAt) }}</div>
              <div class="col-12 col-lg-6"><strong>Published:</strong> {{ formatDateTime(selectedContent.publishedAt) }}</div>
              <div class="col-12 col-lg-6"><strong>Source URL:</strong> {{ selectedContent.sourceUrl || '—' }}</div>
              <div v-if="isSelectedContentPublished" class="col-12">
                <strong>Ссылки просмотра:</strong>
                <span v-if="!publishedContentLinks.length" class="text-muted"> —</span>
                <div v-else class="admin-link-list">
                  <a
                    v-for="link in publishedContentLinks"
                    :key="`published-link-${link.href}`"
                    :href="link.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="admin-link-chip"
                  >
                    {{ link.label }}
                  </a>
                </div>
              </div>
            </div>

            <div v-if="contentFormError" class="alert alert-warning py-2 px-3 mt-2 mb-0">
              {{ contentFormError }}
            </div>

            <div v-if="contentFormSections.length" class="mt-2">
              <section v-for="section in contentFormSections" :key="`content-section-${section.id}`" class="admin-content-section">
                <div class="admin-content-section__title">{{ section.title || section.id }}</div>
                <div class="row g-2 mt-0">
                  <div v-for="field in section.fields" :key="`content-field-${section.id}-${field.name}`" class="col-12 col-lg-6">
                    <label class="form-label mb-1">
                      {{ field.label || field.name }}
                      <span v-if="field.required" class="text-danger">*</span>
                    </label>

                    <template v-if="isJsonField(field)">
                      <pre class="admin-json-preview">{{ formatJsonNode(getContentFieldValue(field.name)) }}</pre>
                    </template>
                    <template v-else-if="isMarkdownField(field)">
                      <textarea
                        :rows="fieldRows(field)"
                        :value="formatFieldValue(getContentFieldValue(field.name))"
                        class="form-control form-control-sm"
                        readonly
                      ></textarea>
                    </template>
                    <template v-else>
                      <input
                        :value="formatFieldValue(getContentFieldValue(field.name))"
                        type="text"
                        class="form-control form-control-sm"
                        readonly
                      />
                    </template>

                    <img
                      v-if="isImageField(field) && getContentFieldValue(field.name)"
                      :src="getContentFieldValue(field.name)"
                      alt=""
                      class="admin-image-preview mt-2"
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
          <div v-else class="text-muted">Выберите контент из таблицы для просмотра полной карточки.</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import AdminJsonTextarea from '@/admin/components/AdminJsonTextarea.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import {
  getAdminStreamById,
  getAdminStreamContentById,
  getAdminStreamContents,
  getAdminStreams,
  getStreamContentFormByType,
  updateAdminStream,
} from '@/admin/api/adminService'
import { CONTENT_STATUS_VALUES } from '@/admin/types/adminContracts'
import { formatDateTime, parseJsonInput, stringifyJsonInput } from '@/admin/utils/adminApiUtils'

const streams = ref([])
const selectedStream = ref(null)
const selectedStreamId = ref(null)

const filters = reactive({
  userId: '',
  username: '',
  type: '',
})

const pagination = reactive({
  page: 0,
  size: 20,
  totalElements: 0,
  totalPages: 0,
})

const sortState = reactive({
  sortBy: 'name',
  sortDir: 'asc',
})

const streamForm = reactive({
  name: '',
  description: '',
  type: '',
})

const settingsText = ref('')
const settingsValidation = ref({ isValid: true, error: '' })
const streamContents = ref([])
const selectedContent = ref(null)
const selectedContentId = ref(null)
const contentForm = ref(null)
const contentFormError = ref('')

const contentFilters = reactive({
  statuses: [],
})

const contentsPagination = reactive({
  page: 0,
  size: 20,
  totalElements: 0,
  totalPages: 0,
})

const contentsSort = reactive({
  sortBy: 'updatedAt',
  sortDir: 'desc',
})

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  execute,
} = useAdminRequestState()

const paginationDisplayTotalPages = computed(() => Math.max(1, pagination.totalPages || 0))
const displayPage = computed(() => (pagination.totalPages ? pagination.page + 1 : 1))
const canGoPrev = computed(() => pagination.page > 0)
const canGoNext = computed(() => pagination.page + 1 < pagination.totalPages)
const contentPaginationDisplayTotalPages = computed(() => Math.max(1, contentsPagination.totalPages || 0))
const contentDisplayPage = computed(() => (contentsPagination.totalPages ? contentsPagination.page + 1 : 1))
const canGoContentsPrev = computed(() => contentsPagination.page > 0)
const canGoContentsNext = computed(() => contentsPagination.page + 1 < contentsPagination.totalPages)
const contentFormSections = computed(() => {
  const sections = Array.isArray(contentForm.value?.sections) ? contentForm.value.sections : []
  return [...sections].sort((a, b) => Number(a.order) - Number(b.order))
})
const isSelectedContentPublished = computed(() => String(selectedContent.value?.status || '').toUpperCase() === 'PUBLISHED')
const publishedContentLinks = computed(() => {
  if (!isSelectedContentPublished.value || !selectedContent.value) return []

  const links = []
  const sourceUrlLink = normalizeExternalUrl(selectedContent.value?.sourceUrl)
  const telegramLink = buildTelegramPublishedLink()
  const wordpressLink = buildWordpressPublishedLink()

  if (telegramLink) links.push({ label: 'Telegram', href: telegramLink })
  if (wordpressLink) links.push({ label: 'WordPress', href: wordpressLink })
  if (sourceUrlLink) links.push({ label: 'Source URL', href: sourceUrlLink })

  const seen = new Set()
  return links.filter(link => {
    if (!link.href || seen.has(link.href)) return false
    seen.add(link.href)
    return true
  })
})

const sortMark = sortBy => {
  if (sortState.sortBy !== sortBy) return ''
  return sortState.sortDir === 'asc' ? '↑' : '↓'
}

const contentSortMark = sortBy => {
  if (contentsSort.sortBy !== sortBy) return ''
  return contentsSort.sortDir === 'asc' ? '↑' : '↓'
}

const toggleSort = sortBy => {
  if (sortState.sortBy === sortBy) {
    sortState.sortDir = sortState.sortDir === 'asc' ? 'desc' : 'asc'
  } else {
    sortState.sortBy = sortBy
    sortState.sortDir = 'asc'
  }
  loadStreams({ page: 0 })
}

const toggleContentSort = sortBy => {
  if (contentsSort.sortBy === sortBy) {
    contentsSort.sortDir = contentsSort.sortDir === 'asc' ? 'desc' : 'asc'
  } else {
    contentsSort.sortBy = sortBy
    contentsSort.sortDir = sortBy === 'updatedAt' || sortBy === 'publishedAt' ? 'desc' : 'asc'
  }
  loadStreamContents({ page: 0 })
}

const isStatusSelected = status => contentFilters.statuses.includes(status)

const toggleStatusFilter = status => {
  if (isStatusSelected(status)) {
    contentFilters.statuses = contentFilters.statuses.filter(item => item !== status)
  } else {
    contentFilters.statuses = [...contentFilters.statuses, status]
  }
  loadStreamContents({ page: 0 })
}

const clearStatusFilters = () => {
  contentFilters.statuses = []
  loadStreamContents({ page: 0 })
}

const formatJsonNode = value => {
  const normalized = stringifyJsonInput(value)
  return normalized || '—'
}

const getContentFieldValue = fieldName => {
  if (!selectedContent.value || !fieldName) return null
  return selectedContent.value[fieldName]
}

const normalizeFieldType = field => String(field?.type || '').trim().toLowerCase()

const isJsonField = field => normalizeFieldType(field) === 'json'

const isMarkdownField = field => {
  const type = normalizeFieldType(field)
  return ['markdown', 'text', 'textarea'].includes(type)
}

const isImageField = field => normalizeFieldType(field) === 'image'

const fieldRows = field => {
  if (normalizeFieldType(field) === 'markdown') return 10
  if (Number(field?.maxLength || 0) >= 1200) return 8
  return 4
}

const formatFieldValue = value => {
  if (value == null || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const normalizeExternalUrl = value => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return raw
  if (/^\/\//.test(raw)) return `https:${raw}`
  return `https://${raw}`
}

const buildTelegramPublishedLink = () => {
  const streamType = String(selectedStream.value?.type || '').toUpperCase()
  if (streamType !== 'TELEGRAM') return ''

  const messageId = String(selectedContent.value?.externalId || '').trim()
  let chatId = String(selectedStream.value?.settings?.chatId || '').trim()
  if (!messageId || !chatId) return ''

  if (chatId.startsWith('@')) chatId = chatId.slice(1)
  if (/^-?\d+$/.test(chatId)) {
    const clean = chatId.replace(/^-100/, '').replace(/^-/, '')
    return `https://t.me/c/${clean}/${messageId}`
  }
  return `https://t.me/${chatId}/${messageId}`
}

const buildWordpressPublishedLink = () => {
  const streamType = String(selectedStream.value?.type || '').toUpperCase()
  if (streamType !== 'WORDPRESS') return ''

  const externalId = String(selectedContent.value?.externalId || '').trim()
  const slug = String(selectedContent.value?.slug || '').trim().replace(/^\/+|\/+$/g, '')
  let base = String(selectedStream.value?.settings?.host || selectedStream.value?.settings?.siteUrl || '').trim()
  if (!externalId || !slug || !base) return ''

  base = normalizeExternalUrl(base)
  try {
    const normalized = base.endsWith('/') ? base : `${base}/`
    return new URL(slug, normalized).toString()
  } catch {
    return ''
  }
}

const loadContentForm = async streamType => {
  const normalizedType = String(streamType || '').trim()
  contentForm.value = null
  contentFormError.value = ''

  if (!normalizedType) {
    contentFormError.value = 'У стрима не указан тип.'
    return
  }

  const response = await execute(
    async () => {
      try {
        return await getStreamContentFormByType(normalizedType)
      } catch (error) {
        const status = Number(error?.response?.status || 0)
        if (status === 404) return null
        throw error
      }
    },
    {
      error: `Не удалось загрузить форму контента для типа ${normalizedType}.`,
    }
  )

  if (!response) {
    contentFormError.value = `Форма контента для типа ${normalizedType} не найдена.`
    return
  }

  contentForm.value = response
}

const fillStreamForm = stream => {
  selectedStream.value = stream
  selectedStreamId.value = stream?.id || null
  streamForm.name = stream?.name || ''
  streamForm.description = stream?.description || ''
  streamForm.type = stream?.type || ''
  settingsText.value = stringifyJsonInput(stream?.settings)
}

const resetContentsState = () => {
  streamContents.value = []
  selectedContent.value = null
  selectedContentId.value = null
  contentForm.value = null
  contentFormError.value = ''
  contentsPagination.page = 0
  contentsPagination.totalElements = 0
  contentsPagination.totalPages = 0
}

const resetFilters = () => {
  filters.userId = ''
  filters.username = ''
  filters.type = ''
  pagination.page = 0
  loadStreams({ page: 0 })
}

const loadStreams = async ({ page = pagination.page } = {}) => {
  clearMessages()
  const result = await execute(
    () =>
      getAdminStreams({
        userId: filters.userId,
        username: filters.username,
        type: filters.type,
        page,
        size: pagination.size,
        sortBy: sortState.sortBy,
        sortDir: sortState.sortDir,
      }),
    {
      error: 'Не удалось загрузить стримы.',
    }
  )

  streams.value = result.content
  pagination.page = result.page
  pagination.size = result.size
  pagination.totalElements = result.totalElements
  pagination.totalPages = result.totalPages

  if (selectedStreamId.value && !streams.value.some(item => item.id === selectedStreamId.value)) {
    selectedStream.value = null
    selectedStreamId.value = null
    resetContentsState()
  }
}

const loadStreamContents = async ({ page = contentsPagination.page } = {}) => {
  if (!selectedStreamId.value) {
    resetContentsState()
    return
  }

  const result = await execute(
    () =>
      getAdminStreamContents(selectedStreamId.value, {
        page,
        size: contentsPagination.size,
        sortBy: contentsSort.sortBy,
        sortDir: contentsSort.sortDir,
        statuses: contentFilters.statuses,
      }),
    {
      error: 'Не удалось загрузить контент стрима.',
    }
  )

  streamContents.value = result.content
  contentsPagination.page = result.page
  contentsPagination.size = result.size
  contentsPagination.totalElements = result.totalElements
  contentsPagination.totalPages = result.totalPages
}

const openDetails = async streamId => {
  clearMessages()
  const stream = await execute(() => getAdminStreamById(streamId), {
    error: 'Не удалось загрузить детали стрима.',
  })
  fillStreamForm(stream)
  resetContentsState()
  await loadContentForm(stream.type)
  await loadStreamContents({ page: 0 })
}

const openContentDetails = async contentId => {
  if (!selectedStreamId.value) {
    pushError('Сначала выберите стрим.')
    return
  }

  const content = await execute(() => getAdminStreamContentById(selectedStreamId.value, contentId), {
    error: 'Не удалось загрузить контент.',
  })
  selectedContent.value = content
  selectedContentId.value = content.id
}

const reloadSelectedStream = async () => {
  if (!selectedStreamId.value) return
  const stream = await execute(() => getAdminStreamById(selectedStreamId.value), {
    error: 'Не удалось загрузить детали стрима.',
  })
  fillStreamForm(stream)
  await loadContentForm(stream.type)
  await loadStreamContents({ page: contentsPagination.page })
}

const submitStreamUpdate = async () => {
  if (!selectedStreamId.value) {
    pushError('Сначала выберите стрим.')
    return
  }

  if (!settingsValidation.value.isValid) {
    pushError(settingsValidation.value.error || 'Невалидный JSON в settings.')
    return
  }

  const settingsParsed = parseJsonInput(settingsText.value, { allowEmpty: true })
  if (!settingsParsed.isValid) {
    pushError(settingsParsed.error)
    return
  }

  const payload = {
    name: streamForm.name || null,
    description: streamForm.description || null,
  }

  if (settingsText.value.trim()) {
    payload.settings = settingsParsed.value
  }

  const updated = await execute(() => updateAdminStream(selectedStreamId.value, payload), {
    success: 'Стрим обновлен.',
    error: 'Не удалось обновить стрим.',
  })

  fillStreamForm(updated)
  streams.value = streams.value.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
  await loadStreams()
}

const goToPrevPage = async () => {
  if (!canGoPrev.value) return
  await loadStreams({ page: pagination.page - 1 })
}

const goToNextPage = async () => {
  if (!canGoNext.value) return
  await loadStreams({ page: pagination.page + 1 })
}

const goToContentsPrevPage = async () => {
  if (!canGoContentsPrev.value) return
  await loadStreamContents({ page: contentsPagination.page - 1 })
}

const goToContentsNextPage = async () => {
  if (!canGoContentsNext.value) return
  await loadStreamContents({ page: contentsPagination.page + 1 })
}

onMounted(async () => {
  await loadStreams({ page: 0 })
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-sort-btn {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  padding: 0;
}

.admin-table__row--active td {
  background: #eef5ff;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}

.admin-detail__subtitle {
  color: #4f6180;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
}

.admin-empty-state {
  border: 1px dashed #d6e3f8;
  border-radius: 12px;
  padding: 18px;
  color: #5f6f89;
  background: #fcfdff;
}

.admin-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.admin-content-surface {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-content-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-statuses {
  min-height: 31px;
  border: 1px solid #d7e3f8;
  border-radius: 8px;
  background: #fff;
  padding: 4px 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.admin-content-detail {
  border-top: 1px solid #e5edf9;
  padding-top: 10px;
}

.admin-content-section {
  border: 1px solid #e2ebfb;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
}

.admin-content-section + .admin-content-section {
  margin-top: 10px;
}

.admin-content-section__title {
  color: #304b75;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.admin-json-preview {
  border: 1px solid #dfe9fa;
  border-radius: 8px;
  background: #fff;
  padding: 8px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 220px;
  overflow: auto;
}

.admin-image-preview {
  max-width: 100%;
  max-height: 220px;
  border: 1px solid #dce7fa;
  border-radius: 8px;
  display: block;
}

.admin-link-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.admin-link-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #cfe0fb;
  background: #f3f8ff;
  color: #24508d;
  text-decoration: none;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
}

.admin-link-chip:hover {
  color: #173965;
  border-color: #b7cff5;
  background: #eaf3ff;
}
</style>
